/**
 * Forum Category Card Component
 * 
 * Modern card display for forum categories with boards
 */

import React from 'react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { MessageCircle, Lock, TrendingUp, Clock, ChevronRight } from 'lucide-react'
import type { ForumCategory } from '@/lib/services/forumStore.service'
import { useTranslation } from 'react-i18next';

interface ForumCategoryCardProps {
  category: ForumCategory
}

export default function ForumCategoryCard({ category }: ForumCategoryCardProps) {
  const { t } = useTranslation();
  return (
    <Card className="overflow-hidden border-card-border hover:border-primary/50 transition-all duration-300 group">
      {/* Category Header */}
      <div className="relative bg-gradient-to-r from-primary via-primary to-accent p-6">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtMS4xLS45LTItMi0yaC0yYy0xLjEgMC0yIC45LTIgMnYyYzAgMS4xLjkgMiAyIDJoMmMxLjEgMCAyLS45IDItMnYtMnptMCAyNGMwLTEuMS0uOS0yLTItMmgtMmMtMS4xIDAtMiAuOS0yIDJ2MmMwIDEuMS45IDIgMiAyaDJjMS4xIDAgMi0uOSAyLTJ2LTJ6bS0yNCAwYzAtMS4xLS45LTItMi0yaC0yYy0xLjEgMC0yIC45LTIgMnYyYzAgMS4xLjkgMiAyIDJoMmMxLjEgMCAyLS45IDItMnYtMnptMC0yNGMwLTEuMS0uOS0yLTItMmgtMmMtMS4xIDAtMiAuOS0yIDJ2MmMwIDEuMS45IDIgMiAyaDJjMS4xIDAgMi0uOSAyLTJ2LTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
        <div className="relative flex items-center gap-3">
          <div className="text-4xl">{category.icon}</div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-primary-foreground mb-1">
              {category.name}
            </h2>
            {category.description && (
              <p className="text-primary-foreground/90 text-sm">
                {category.description}
              </p>
            )}
          </div>
          <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
            {category.boards.length} {t(category.boards.length === 1 ? 'store.forum.board' : 'store.forum.boards')}
          </Badge>
        </div>
      </div>

      {/* Boards List */}
      <div className="divide-y divide-border">
        {category.boards.map((board) => (
          <Link
            key={board.id}
            to={`/forum/${category.slug}/${board.slug}`}
            className="block p-6 hover:bg-accent/5 transition-all duration-200 group/board"
          >
            <div className="flex items-start gap-4">
              {/* Board Icon */}
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 group-hover/board:scale-110 transition-transform duration-300 shadow-sm"
                style={{
                  backgroundColor: board.color + '15',
                  border: `2px solid ${board.color}30`,
                }}
              >
                {board.icon}
              </div>

              {/* Board Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-bold text-foreground group-hover/board:text-primary transition-colors">
                    {board.name}
                  </h3>
                  {board.isLocked && (
                    <Lock className="w-4 h-4 text-muted-foreground" />
                  )}
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover/board:text-primary group-hover/board:translate-x-1 transition-all ml-auto" />
                </div>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {board.description}
                </p>

                {/* Board Stats */}
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <MessageCircle className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-foreground">
                        {board.topicsCount}
                      </div>
                      <div className="text-xs text-muted-foreground">Tópicos</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-foreground">
                        {board.postsCount}
                      </div>
                      <div className="text-xs text-muted-foreground">Posts</div>
                    </div>
                  </div>

                  {board.lastPost && (
                    <div className="hidden lg:flex items-center gap-2 ml-auto max-w-xs">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Clock className="w-4 h-4 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs text-muted-foreground">{t('store.forum.lastPost')}</div>
                        <div className="text-xs font-medium text-foreground truncate">
                          por {board.lastPost.authorName}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </Card>
  )
}
